import axios, { AxiosInstance } from 'axios';
import apiClient from '.';

export const MAX_FILE_SIZE = {
    value: 50000000,
    text: '50MB',
};

export const MAX_FILE_SIZE_TEXT = '50MB';

export enum FileStatusType {
    VALIDATION_SUCCESS = 'validation-success',
    ANONYMIZE_VALIDATION_SUCCESS = 'anonymize-validation-success',
    VALIDATION_ERROR = 'validation-error',
    READY_TO_VALIDATE = 'ready-to-validate',
    VALIDATION_IN_PROGRESS = 'validation-in-progress',
    IN_PROGRESS = 'in-progress',
    FILE_READING_ERROR = 'file-reading-error',
    FILE_SIZE_EXCEEDED = 'file-size-exceeded',
    UPLOAD_SUCCESS = 'upload-success',
    UPLOAD_ERROR = 'upload-error',
    CREATE_SUCCESS = 'create-success',
    ANONYMIZE_SUCCESS = 'anonymize-success',
    ANONYMIZE_ERROR = 'anonymize-error',
    CREATE_ERROR = 'create-error',
    SUCCESS = 'success',
    ANONYMIZE_SUCCESS_STATUS = 'Success',
    ANONYMIZE_CONVERTING_STATUS = 'Converting',
    ERROR = 'error',
    READY_TO_UPDATE = 'ready-to-update',
    UPDATE_SUCCESS = 'update-success',
    UPDATE_ERROR = 'update-error',
}

export const validateFile = async (
    files: Record<string, any>[],
    requireValidation: boolean = false
) => {
    try {
        const axiosInstance: AxiosInstance = axios.create({
            baseURL: process.env.REACT_APP_EPHEMERALSTORE,
            headers: {
                ...apiClient.headers,
                'Content-type': 'application/json',
            },
        });
        const response = await Promise.all(
            files.map(async (file: Record<string, any>) => {
                if (file?.status !== FileStatusType.IN_PROGRESS) {
                    return { ...file };
                }
                if (file?.size > MAX_FILE_SIZE?.value) {
                    return {
                        ...file,
                        status: FileStatusType.FILE_SIZE_EXCEEDED,
                        message: '',
                    };
                }
                try {
                    let { data } = await axiosInstance.post(
                        `api/v1/upload/validate_filename`,
                        { filename: file.name }
                    );
                    data.status =
                        data.status === 'ok'
                            ? requireValidation
                                ? FileStatusType.READY_TO_VALIDATE
                                : file.isAnonymize === 'anonymization'
                                ? FileStatusType.ANONYMIZE_VALIDATION_SUCCESS
                                : FileStatusType.VALIDATION_SUCCESS
                            : FileStatusType.VALIDATION_ERROR;
                    return {
                        ...file,
                        ...data,
                        message: '',
                    };
                } catch (e: any) {
                    return {
                        ...file,
                        status: FileStatusType.VALIDATION_ERROR,
                        message:
                            e?.response?.data?.error ||
                            e?.response?.data?.message ||
                            '',
                    };
                }
            })
        );
        return response;
    } catch (e) {
        console.log(e);
    }
};

export const uploadFiles = async (files: any) => {
    try {
        const axiosInstance: AxiosInstance = axios.create({
            baseURL: process.env.REACT_APP_EPHEMERALSTORE,
            headers: {
                ...apiClient.headers,
                'Content-type': 'multipart/form-data',
            },
        });
        return await Promise.all(
            files.map(async ({ binary, ...file }: Record<string, any>) => {
                try {
                    if (!binary) {
                        return {
                            ...file,
                            status: 'file-reading-error',
                            message: '',
                        };
                    }

                    const data = new FormData();
                    data.append('file', binary as Blob);
                    if (
                        file.fileType === 'Statement' ||
                        file.isAnonymize === 'anonymization'
                    ) {
                        data.append('validate_password', 'true');
                    }
                    if (file.password) {
                        data.append('password', file.password);
                    }
                    const response = await axiosInstance.post(
                        `api/v1/upload`,
                        data
                    );
                    return {
                        ...file,
                        ...response?.data,
                        status: FileStatusType.UPLOAD_SUCCESS,
                        message: '',
                    };
                } catch (e: any) {
                    return {
                        ...file,
                        status: FileStatusType.UPLOAD_ERROR,
                        message:
                            e?.response?.data?.error ||
                            e?.response?.data?.message ||
                            '',
                    };
                }
            })
        );
    } catch (e: any) {
        return files.map((file: any) => {
            return {
                ...file,
                status: FileStatusType.UPLOAD_ERROR,
                message:
                    e?.response?.data?.error ||
                    e?.response?.data?.message ||
                    '',
            };
        });
    }
};

export const createFiles = async (
    files: any,
    parent_user_ids: number[] | null,
    fileType?: string,
    validate_only: boolean = false
) => {
    try {
        return await Promise.all(
            files.map(async (file: Record<string, any>) => {
                if (
                    [
                        FileStatusType.ANONYMIZE_SUCCESS_STATUS,
                        FileStatusType.UPLOAD_SUCCESS,
                        FileStatusType.VALIDATION_SUCCESS,
                        FileStatusType.ANONYMIZE_SUCCESS,
                        'Converting',
                    ].includes(file?.status)
                ) {
                    try {
                        const { data: response }: any = await apiClient.post(
                            `documents`,
                            {
                                data: {
                                    parent_user_ids: parent_user_ids
                                        ? [parent_user_ids]
                                        : null,
                                    file_key: file?.key,
                                    original_filename:
                                        file?.originalFileName || null,
                                    description: file?.description,
                                    file_password: file?.password,
                                    fileType: fileType || null,
                                    validate_only,
                                },
                            }
                        );
                        if (response?.success) {
                            return {
                                ...file,
                                documentId: response?.records?.[0]?.documentId,
                                status: validate_only
                                    ? FileStatusType.VALIDATION_SUCCESS
                                    : fileType === 'Statement'
                                    ? FileStatusType.SUCCESS
                                    : FileStatusType.CREATE_SUCCESS,
                                message: '',
                            };
                        }
                        return {
                            ...file,
                            documentId: null,
                            status: validate_only
                                ? FileStatusType.VALIDATION_ERROR
                                : FileStatusType.CREATE_ERROR,
                            message: '',
                        };
                    } catch (e: any) {
                        return {
                            ...file,
                            status: validate_only
                                ? FileStatusType.VALIDATION_ERROR
                                : FileStatusType.CREATE_ERROR,
                            documentId: null,
                            message:
                                e?.response?.data?.error ||
                                e?.response?.data?.message ||
                                '',
                        };
                    }
                } else {
                    return file;
                }
            })
        );
    } catch (e: any) {
        return files.map((file: any) => {
            return {
                ...file,
                status: FileStatusType.CREATE_ERROR,
                message:
                    e?.response?.data?.error ||
                    e?.response?.data?.message ||
                    '',
            };
        });
    }
};

export const anonymizeFiles = async (file: any, addMoreText: any) => {
    const sendResponse = ({ file, response, id = '', message = '' }: any) => {
        return {
            ...file,
            status: response
                ? FileStatusType.ANONYMIZE_SUCCESS
                : FileStatusType.ANONYMIZE_ERROR,
            id,
            message,
        };
    };
    try {
        const substitutions: any = {};
        addMoreText.forEach((obj: any) => {
            substitutions[obj.textToReplace] = obj.textToReplaceWith;
        });
        const { data: response }: any = await apiClient.post(
            `documents/anonymize`,
            {
                data: {
                    document_key: file?.key,
                    password: file?.password,
                    substitutions,
                    customer_database_priority: '',
                },
            }
        );
        return sendResponse({
            file,
            response,
            id: response[0]?.id,
        });
    } catch (e: any) {
        return sendResponse({
            file,
            status: 'error',
            response: null,
            message:
                e?.response?.data?.error || e?.response?.data?.message || '',
        });
    }
};

export const updateFiles = async (files: Record<string, any>[]) => {
    try {
        return await Promise.all(
            files.map(async (file: Record<string, any>) => {
                if (file?.status !== FileStatusType.UPDATE_SUCCESS) {
                    try {
                        let { data: response }: any = await apiClient.put(
                            `documents`,
                            {
                                data: {
                                    file_key: file?.key,
                                    description: file?.description,
                                    file_password: file?.password,
                                    document_id: file?.documentId,
                                },
                            }
                        );
                        if (response?.success) {
                            return {
                                ...file,
                                documentId: response?.records?.[0]?.documentId,
                                status: FileStatusType.UPDATE_SUCCESS,
                                message: '',
                            };
                        }
                        return {
                            ...file,
                            documentId: null,
                            status: FileStatusType.UPDATE_ERROR,
                            message: '',
                        };
                    } catch (e: any) {
                        return {
                            ...file,
                            status: FileStatusType.UPDATE_ERROR,
                            documentId: null,
                            message:
                                e?.response?.data?.error ||
                                e?.response?.data?.message ||
                                '',
                        };
                    }
                } else {
                    return file;
                }
            })
        );
    } catch (e: any) {
        return files.map((file) => {
            return {
                ...file,
                status: 'create-error',
                message:
                    e?.response?.data?.error ||
                    e?.response?.data?.message ||
                    '',
            };
        });
    }
};

export const addTags = async (files: any) => {
    try {
        return await Promise.all(
            files.map(async (file: Record<string, any>) => {
                if (file?.status === 'create-success') {
                    try {
                        let { data: response }: any = await tagsApi(file);
                        return {
                            ...file,
                            ...response,
                            status: 'success',
                            message: '',
                        };
                    } catch (e: any) {
                        return {
                            ...file,
                            status: 'error',
                            message:
                                e?.response?.data?.error ||
                                e?.response?.data?.message ||
                                'error',
                        };
                    }
                } else {
                    return file;
                }
            })
        );
    } catch (e: any) {
        return files.map((file: any) => {
            return {
                ...file,
                status: 'create-error',
                message:
                    e?.response?.data?.error ||
                    e?.response?.data?.message ||
                    '',
            };
        });
    }
};

export const updateTags = async (files: any) => {
    try {
        return await Promise.all(
            files.map(async (file: Record<string, any>) => {
                if (
                    file?.status !== 'update-tags-succes' ||
                    file?.status !== 'update-succes'
                ) {
                    try {
                        let { data: response }: any = await tagsApi(file);
                        return {
                            ...file,
                            ...response,
                            status: 'update-tags-success',
                            message: '',
                        };
                    } catch (e: any) {
                        return {
                            ...file,
                            status: 'update-error',
                            message:
                                e?.response?.data?.error ||
                                e?.response?.data?.message ||
                                'update-error',
                        };
                    }
                } else {
                    return file;
                }
            })
        );
    } catch (e: any) {
        return files.map((file: any) => {
            return {
                ...file,
                status: 'create-error',
                message:
                    e?.response?.data?.error ||
                    e?.response?.data?.message ||
                    '',
            };
        });
    }
};

const tagsApi = (file: any) => {
    return apiClient.post(`documents/tags`, {
        data: {
            taggable_id: file?.documentId,
            transaction_tag_value: file?.transactionTags,
            security_tag_value: file?.securityTags,
            custom_tag_value: file?.customTags.flat(),
        },
    });
};

export const uploadTemplate = async (params: any) =>
    await apiClient.get(`uploads/templates`, {
        queryParams: params,
        headers: {
            ...apiClient.headers,
        },
    });

export const fetchAnonymizeStatus = async (
    file: any,
    output_format = 'JSON'
) => {
    try {
        let { data: response }: any = await apiClient.get(
            `documents/anonymize/status`,
            {
                queryParams: { document_ids: file.id, output_format },
            }
        );
        return {
            ...file,
            ...response,
            key: response.fileKey || null,
            status: response.stateMessage || null,
            warning: response.warnings || [],
        };
    } catch (e: any) {
        return {
            ...file,
            status: 'error',
            message:
                e?.response?.data?.error ||
                e?.response?.data?.message ||
                'error',
        };
    }
};
